import * as Sentry from '@sentry/vue';
import router from '@/router';
import {getEnvMode, isProduction} from '@/utils/env';
import {isIgnorableAxiosTransportNoise, isIgnorableClientNoise} from '@/utils/sentryNoise';
import {isStaleAssetError} from '@/utils/staleAssetReload';
import {d} from '@/utils/consoleHelper.ts';
import type {App} from 'vue';

const IS_DEBUG = !isProduction();

export function initSentry(app: App) {
    const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || 'https://477434e9051c46e38a6d5052ba4129d7@sentry.io/2158517';

    if (!SENTRY_DSN) {
        d.warn('⚠️ Sentry DSN is not provided.');
        return;
    }

    Sentry.init({
        app,
        dsn: SENTRY_DSN,
        release: import.meta.env.VITE_APP_VERSION || '1.0.52',
        // debug: IS_DEBUG,
        environment: getEnvMode(),

        // Belt-and-suspenders with beforeSend for string/`Error: ga` onerror noise (VUE-9N2)
        // and Instagram / WKWebView bridge strings (VUE-96B, VUE-9HK).
        ignoreErrors: [
            /^ga$/,
            /^fa$/,
            /window\.webkit\.messageHandlers/i,
            /Java object is gone/i,
        ],

        beforeSend: (event, hint) => {
            const originalException = hint.originalException ?? hint.syntheticException;
            // Vue / onerror often put the useful message only on the event (VUE-6QR, VUE-8MY).
            const eventMessage = event.exception?.values?.[0]?.value ?? event.message;

            if (
                isStaleAssetError(originalException)
                || isStaleAssetError(eventMessage)
                || isIgnorableClientNoise(originalException, event)
                || isIgnorableClientNoise(eventMessage, event)
                || isIgnorableAxiosTransportNoise(originalException)
                || isIgnorableAxiosTransportNoise(eventMessage)
            ) {
                return null;
            }

            if (IS_DEBUG) {
                d.error('[Sentry Debug] Captured Error:', originalException);
                // return null; // this drops the event and nothing will be sent to sentry
            }
            return event;
        },

        integrations: [
            Sentry.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                },
            }),
            Sentry.browserTracingIntegration({router}),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: true,
            }),
            // The following is all you need to enable canvas recording with Replay
            Sentry.replayCanvasIntegration(),
        ],

        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: IS_DEBUG ? 1.0 : 0.1,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        tracesSampleRate: IS_DEBUG ? 1.0 : 0.1,
        // Anchor-match only — bare 'fleetwire.io' also hits GA collect URLs that
        // embed our host in query params and can break those requests via CORS.
        tracePropagationTargets: [
            'localhost',
            /^https?:\/\/([^/]+\.)?fleetwire\.test($|\/)/,
            /^https?:\/\/([^/]+\.)?fleetwire\.io($|\/)/,
            /^\//,
        ],
    });

    if (IS_DEBUG) {
        d.log('✅ Sentry Initialized Successfully!');
    }
}